import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { DropdownMenuTypePropType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';

import { ShipFromDropdownRenderer } from './ship-from-dropdown-renderer';
import { CUSTOMERS_CREATE_LABEL } from '../../enums/csw-types';
import { AnalyticsService } from '../../services/analytics-service';
import { DEFAULTS } from '../../ship-from/components/ship-from-dropdown/constants';
import { ValidationPropType } from '../../ship-from/components/ship-from-dropdown/prop-types';
import { startUpdateAvailableShippingServices } from '../../store/csw/csw.actions';
import { EXTERNAL_SHIP_FROM_PREFIX } from '../../store/csw/csw.fulfillment-plan-to-csw';
import { updateCustomerCreateLabelShipFromId } from '../../store/customers/create-label/customers.create-label.actions';
import Global from '../../store/global-selectors';
import { useSortedStrings } from '../../hooks/use-sorted-translations';
import { useFulfillmentPlannerContext } from '../context';

const sortOptions = { labelProp: 'name' };

export const ShipFrom = ({
  id,
  dropdownMenuType,
  isDisabled = false,
  showFulfillmentProviders = false,
  validation = DEFAULTS.validation,
  singleItemTitleClassName,
}) => {
  const { shipFromId, onShipFromChange, fulfillmentPlanId, parentComponentName, isReturn } = useFulfillmentPlannerContext();
  const dispatch = useDispatch();

  const onAddShipFromSuccess = useCallback(
    (newShipFromId) => {
      dispatch(startUpdateAvailableShippingServices(fulfillmentPlanId, newShipFromId));
      onShipFromChange(newShipFromId);

      if (fulfillmentPlanId === CUSTOMERS_CREATE_LABEL) {
        dispatch(updateCustomerCreateLabelShipFromId(newShipFromId));
      }
    },
    [dispatch, fulfillmentPlanId, onShipFromChange],
  );

  const fulfillmentProviders = useSelector(Global.selectSellerActiveFulfillmentProviders);
  const fulfillmentShipFroms = showFulfillmentProviders
    ? fulfillmentProviders.map((provider) => ({
        name: provider.name,
        id: `${EXTERNAL_SHIP_FROM_PREFIX}${provider.sellerFulfillmentProviderId}`,
      }))
    : [];

  const fulfillmentProviderWarehouses = useSelector(Global.selectSellerFulfillmentProviderWarehouses);
  const fulfillmentProviderWarehouseShipFroms = isReturn
    ? fulfillmentProviderWarehouses.map((shipFrom) => ({
        name: shipFrom.name,
        id: shipFrom.shipFromId,
      }))
    : [];

  const shipFroms = useSelector(Global.selectSellerShipFroms)
    .map(({ shipFromId: _shipFromId, name }) => ({ id: _shipFromId, name }))
    .concat(fulfillmentShipFroms)
    .concat(fulfillmentProviderWarehouseShipFroms);

  const shipFromsSorted = useSortedStrings(shipFroms, sortOptions);

  const onChange = useCallback(
    (_shipFromId) => {
      onShipFromChange(_shipFromId);
      if (shipFromId !== _shipFromId) {
        const eventPayload = {
          shipFromId: _shipFromId,
          oldShipFromId: shipFromId,
          fulfillmentPlanId,
        };
        AnalyticsService.trackManualEvent('csw.ship_from_id_update_manual', eventPayload);
      }
    },
    [fulfillmentPlanId, onShipFromChange, shipFromId],
  );

  return (
    <ShipFromDropdownRenderer
      dropdownMenuType={dropdownMenuType}
      id={id}
      isDisabled={isDisabled}
      onAddShipFromSuccess={onAddShipFromSuccess}
      onChange={onChange}
      parentComponentName={parentComponentName}
      selectedShipFromId={shipFromId}
      shipFroms={shipFromsSorted}
      singleItemTitleClassName={singleItemTitleClassName}
      validation={validation}
    />
  );
};

ShipFrom.propTypes = {
  id: PropTypes.string,
  dropdownMenuType: DropdownMenuTypePropType,
  isDisabled: PropTypes.bool,
  showFulfillmentProviders: PropTypes.bool,
  validation: ValidationPropType,
  singleItemTitleClassName: PropTypes.string,
};
